// openWakeWord in-browser — SPIKE (feasibility probe, not yet wired into the
// portal voice assistant).
//
// Replicates openWakeWord's 3-stage ONNX pipeline client-side via
// onnxruntime-web (WASM, single-thread → no COOP/COEP needed):
//
//   16kHz mono PCM  ──melspectrogram.onnx──▶  mel frames [F,32]  (×1/10 +2)
//   76 mel frames ×16 windows (stride 8)  ──embedding_model.onnx──▶  16×96
//   16 embeddings  ──hey_jarvis_v0.1.onnx──▶  score ∈ [0,1]
//
// The audio never leaves the device — only the score does. When the score
// crosses the threshold we'd trigger the existing startListening() flow; the
// wake word is just the trigger.
//
// NOTE (spike vs. integration): this recomputes the melspectrogram over the
// whole rolling buffer on every tick. Correct + cheap enough to measure
// feasibility; integration will switch to an incremental mel buffer (oWW's
// real streaming) to shave CPU. See the spike page for live latency/CPU.

// WASM-only build: avoids the jsep/webgpu proxy module. The runtime's loader
// glue + .wasm are pulled straight from node_modules via Vite's `?url` (served
// as proper assets) — NOT from /public, which Vite refuses to import as a
// module ("This file is in /public … should not be imported from source code").
import * as ort from "onnxruntime-web/wasm";
import ortWasmUrl from "onnxruntime-web/ort-wasm-simd-threaded.wasm?url";
import ortMjsUrl from "onnxruntime-web/ort-wasm-simd-threaded.mjs?url";

// oWW pipeline constants (fixed by the pre-trained models).
const SAMPLE_RATE = 16000;
const MEL_BINS = 32;
const EMB_WINDOW = 76; // mel frames per embedding
const EMB_STEP = 8; // mel-frame stride between embeddings (= 80ms)
const WAKE_FRAMES = 16; // embeddings the wake model consumes
// Mel frames spanned by 16 stride-8 embedding windows: 76 + 15*8.
const NEEDED_MEL = EMB_WINDOW + (WAKE_FRAMES - 1) * EMB_STEP; // 196
// ~2s rolling audio buffer (196 frames @ hop 160 ≈ 31360 samples; round up).
export const BUFFER_SAMPLES = SAMPLE_RATE * 2;

/**
 * Append `chunk` to a rolling PCM buffer, keeping only the last `max` samples.
 * Pure (no detector state) so the ring math is unit-testable without ort/mic.
 */
export function appendRing(buf: Float32Array, chunk: Float32Array, max: number): Float32Array {
  const merged = new Float32Array(buf.length + chunk.length);
  merged.set(buf, 0);
  merged.set(chunk, buf.length);
  // slice() (copy) not subarray() (view) — a view would pin the whole `merged`
  // backing store alive every tick.
  return merged.length > max ? merged.slice(merged.length - max) : merged;
}

const MODEL_BASE = "/wakeword";

let ortConfigured = false;
function configureOrt() {
  if (ortConfigured) return;
  // Single-thread WASM: avoids SharedArrayBuffer / cross-origin isolation.
  ort.env.wasm.numThreads = 1;
  ort.env.wasm.simd = true;
  // Point ort at the bundler-served runtime files (no CDN, no /public).
  ort.env.wasm.wasmPaths = { wasm: ortWasmUrl, mjs: ortMjsUrl };
  ortConfigured = true;
}

async function load(name: string): Promise<ort.InferenceSession> {
  return ort.InferenceSession.create(`${MODEL_BASE}/${name}`, {
    executionProviders: ["wasm"],
    graphOptimizationLevel: "all",
  });
}

export interface WakeWordStats {
  /** Last wake-model score in [0,1]. */
  score: number;
  /** Wall-clock ms for the last full pipeline tick. */
  inferenceMs: number;
  /** True once the rolling buffer holds enough audio to score. */
  warm: boolean;
}

/**
 * Streaming wake-word detector. Feed 16kHz mono Float32 PCM via {@link push};
 * call {@link tick} on an interval to run the pipeline and read the score.
 */
export class WakeWordDetector {
  private mel!: ort.InferenceSession;
  private emb!: ort.InferenceSession;
  private wake!: ort.InferenceSession;
  private melIn!: string;
  private embIn!: string;
  private wakeIn!: string;
  private melOut!: string;
  private embOut!: string;
  private wakeOut!: string;

  // Rolling 16kHz audio ring (newest at the end). Annotated to the wide
  // Float32Array alias so it matches appendRing's return (TS typed-array
  // buffer-variance: `new Float32Array()` infers the narrow ArrayBuffer form).
  private buf: Float32Array = new Float32Array(0);

  async init(): Promise<void> {
    configureOrt();
    [this.mel, this.emb, this.wake] = await Promise.all([
      load("melspectrogram.onnx"),
      load("embedding_model.onnx"),
      load("hey_jarvis_v0.1.onnx"),
    ]);
    // Read I/O names off the models so we don't hard-code oWW's naming.
    this.melIn = this.mel.inputNames[0];
    this.melOut = this.mel.outputNames[0];
    this.embIn = this.emb.inputNames[0];
    this.embOut = this.emb.outputNames[0];
    this.wakeIn = this.wake.inputNames[0];
    this.wakeOut = this.wake.outputNames[0];
  }

  /** Append a chunk of 16kHz mono PCM, trimming to the rolling window. */
  push(chunk: Float32Array): void {
    this.buf = appendRing(this.buf, chunk, BUFFER_SAMPLES);
  }

  /** Run the pipeline once over the current buffer. Returns score 0 until warm. */
  async tick(): Promise<WakeWordStats> {
    const t0 = performance.now();
    if (this.buf.length < BUFFER_SAMPLES * 0.95) {
      return { score: 0, inferenceMs: performance.now() - t0, warm: false };
    }

    // 1) melspectrogram over the rolling buffer.
    const audio = new ort.Tensor("float32", this.buf.slice(), [1, this.buf.length]);
    const melRes = await this.mel.run({ [this.melIn]: audio });
    const melT = melRes[this.melOut];
    const melData = melT.data as Float32Array;
    const F = melData.length / MEL_BINS;
    if (F < NEEDED_MEL) {
      return { score: 0, inferenceMs: performance.now() - t0, warm: false };
    }
    // oWW normalization.
    for (let i = 0; i < melData.length; i++) melData[i] = melData[i] / 10 + 2;

    // 2) 16 embedding windows over the last NEEDED_MEL mel frames.
    const base = (F - NEEDED_MEL) * MEL_BINS;
    const batch = new Float32Array(WAKE_FRAMES * EMB_WINDOW * MEL_BINS);
    for (let w = 0; w < WAKE_FRAMES; w++) {
      const src = base + w * EMB_STEP * MEL_BINS;
      batch.set(
        melData.subarray(src, src + EMB_WINDOW * MEL_BINS),
        w * EMB_WINDOW * MEL_BINS,
      );
    }
    const embRes = await this.emb.run({
      [this.embIn]: new ort.Tensor("float32", batch, [WAKE_FRAMES, EMB_WINDOW, MEL_BINS, 1]),
    });
    const embData = embRes[this.embOut].data as Float32Array; // [16,1,1,96] → 16*96 contiguous

    // 3) wake model over the 16 embeddings.
    const wakeRes = await this.wake.run({
      [this.wakeIn]: new ort.Tensor("float32", embData.slice(), [1, WAKE_FRAMES, 96]),
    });
    const score = (wakeRes[this.wakeOut].data as Float32Array)[0];

    return { score, inferenceMs: performance.now() - t0, warm: true };
  }

  dispose(): void {
    this.buf = new Float32Array(0);
  }
}

/**
 * Open the mic at 16kHz and stream PCM into a detector. Returns a stop fn.
 * Uses ScriptProcessorNode (SPIKE-grade: deprecated but trivially portable,
 * incl. Safari); integration will move capture to an AudioWorklet.
 */
export async function startWakeMic(
  onChunk: (pcm16k: Float32Array) => void,
): Promise<() => void> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  });
  // Asking the context for 16kHz lets the browser resample the mic for us.
  const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
  const src = ctx.createMediaStreamSource(stream);
  const node = ctx.createScriptProcessor(2048, 1, 1);
  node.onaudioprocess = (e) => onChunk(new Float32Array(e.inputBuffer.getChannelData(0)));
  src.connect(node);
  node.connect(ctx.destination);
  return () => {
    node.disconnect();
    src.disconnect();
    stream.getTracks().forEach((t) => t.stop());
    void ctx.close();
  };
}
